import {
  useSessionId,
  useEppoClient,
  useEppoDefaultSubjectAttributes,
} from '@moonpig/web-core-utils'
import { useMemo } from 'react'
import { logger } from '@moonpig/web-core-monitoring'
import { EppoClient, VariationType } from '@eppo/js-client-sdk-common'
import { useEppoExperimentOverrides } from '@moonpig/web-core-utils/src/eppoContext/EppoContext'
import {
  Attributes,
  DefaultSubjectAttributes,
} from '../../shared/DefaultSubjectAttributes'

type AssignmentFunction<ReturnType> = (args: {
  client: EppoClient
  defaultAttributes: DefaultSubjectAttributes
  sessionId: string
  override: string | undefined
}) => ReturnType

export const useExperimentOld = <ReturnType>(
  experimentKey: string,
  fallback: ReturnType,
  assignmentCallback: AssignmentFunction<ReturnType>,
) => {
  const overrides = useEppoExperimentOverrides()
  const override = overrides?.[experimentKey]

  const { sessionId } = useSessionId()
  const eppoClient = useEppoClient()
  const eppoDefaultSubjectAttributes = useEppoDefaultSubjectAttributes()

  const assignedVariation = useMemo(() => {
    if (!eppoClient) {
      logger.fixToday('useEppoExperiment: Client is not defined')
      return fallback
    }
    if (!eppoDefaultSubjectAttributes) {
      logger.fixToday(
        'useEppoExperiment: Default subject attributes are not defined',
      )
      return fallback
    }

    if (!sessionId) return fallback

    return assignmentCallback({
      client: eppoClient,
      defaultAttributes: eppoDefaultSubjectAttributes,
      sessionId,
      override,
    })
  }, [
    assignmentCallback,
    eppoClient,
    eppoDefaultSubjectAttributes,
    fallback,
    override,
    sessionId,
  ])

  return assignedVariation
}

export const useExperiment = <R>(
  experimentKey: string,
  args: { fallback: R; subjectAttributes?: Attributes },
): R => {
  const overrides = useEppoExperimentOverrides()
  const override = overrides?.[experimentKey]

  const { sessionId } = useSessionId()
  const eppoClient = useEppoClient()

  const eppoDefaultSubjectAttributes = useEppoDefaultSubjectAttributes()

  const assignedVariation = useMemo(() => {
    if (!eppoClient) {
      logger.fixToday('useEppoExperiment: Client is not defined')
      return args.fallback
    }
    if (!eppoDefaultSubjectAttributes) {
      logger.fixToday(
        'useEppoExperiment: Default subject attributes are not defined',
      )
      return args.fallback
    }

    if (!sessionId) return args.fallback

    const type =
      eppoClient.getFlagConfigurations()[experimentKey]?.variationType

    switch (type) {
      case VariationType.STRING:
        if (override) return override
        return eppoClient.getStringAssignment(
          experimentKey,
          sessionId,
          {
            ...eppoDefaultSubjectAttributes,
            ...args.subjectAttributes,
            shouldLog: true,
          },
          args.fallback as string,
        )

      case VariationType.BOOLEAN:
        if (override) return override === 'true'
        return eppoClient.getBooleanAssignment(
          experimentKey,
          sessionId,
          {
            ...eppoDefaultSubjectAttributes,
            ...args.subjectAttributes,
            shouldLog: true,
          },
          args.fallback as boolean,
        )

      case VariationType.INTEGER:
        if (override) return Number(override)
        return eppoClient.getIntegerAssignment(
          experimentKey,
          sessionId,
          {
            ...eppoDefaultSubjectAttributes,
            ...args.subjectAttributes,
            shouldLog: true,
          },
          args.fallback as number,
        )
      case VariationType.NUMERIC:
        if (override) return Number(override)
        return eppoClient.getNumericAssignment(
          experimentKey,
          sessionId,
          {
            ...eppoDefaultSubjectAttributes,
            ...args.subjectAttributes,
            shouldLog: true,
          },
          args.fallback as number,
        )
      case VariationType.JSON:
        if (override) return JSON.parse(override)
        return eppoClient.getJSONAssignment(
          experimentKey,
          sessionId,
          {
            ...eppoDefaultSubjectAttributes,
            ...args.subjectAttributes,
            shouldLog: true,
          },
          args.fallback as object,
        )
      case undefined:
        if (override) return override === 'false' ? '' : override
        return args.fallback
      default:
        logger.fixToday(
          `useEppoExperiment: Experiment ${experimentKey} has an unsupported variation type: ${type}`,
        )
        return args.fallback
    }
  }, [
    experimentKey,
    eppoClient,
    eppoDefaultSubjectAttributes,
    sessionId,
    override,
    args,
  ])

  return assignedVariation
}
